import { useCallback, useEffect, useRef, useState } from 'react'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { ContentEditable } from '@lexical/react/LexicalContentEditable'
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary'
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin'
import { $getRoot } from 'lexical'
import { EditorWrapper, EditorContent, ResizeHandle } from './FormattingStyles'

function Placeholder({ text }) {
    return <div className="editor-placeholder">{text}</div>
}

// Plugin that syncs editor state to JSON on change
function OnChangePlugin({ onChange }) {
    const [editor] = useLexicalComposerContext()

    useEffect(() => {
        return editor.registerUpdateListener(({ editorState }) => {
            editorState.read(() => {
                const root = $getRoot()
                const hasContent = root.getTextContent().trim().length > 0
                const json = hasContent ? JSON.stringify(editorState.toJSON()) : ''
                onChange(json)
            })
        })
    }, [editor, onChange])

    return null
}

// Plugin that loads initial JSON value into editor.
// Watches value but only applies once — when it first arrives non-empty.
// This handles async form data loading without resetting on every keystroke.
function InitValuePlugin({ value }) {
    const [editor] = useLexicalComposerContext()
    const initialized = useRef(false)

    useEffect(() => {
        if (!value || initialized.current) return
        try {
            const parsed = JSON.parse(value)
            const editorState = editor.parseEditorState(parsed)
            editor.setEditorState(editorState)
            initialized.current = true
        } catch {
            // not valid JSON, ignore
        }
    }, [value, editor])

    return null
}

export default function RichTextEditor({ rows, placeholder, value, onChange, resizable }) {
    const [height, setHeight] = useState(null)
    const wrapperRef = useRef(null)

    const handleMouseDown = useCallback((e) => {
        e.preventDefault()
        const startY = e.clientY
        const startHeight = wrapperRef.current?.getBoundingClientRect().height ?? 0

        const onMouseMove = (ev) => {
            const newHeight = Math.max(80, startHeight + (ev.clientY - startY))
            setHeight(newHeight)
        }
        const onMouseUp = () => {
            document.removeEventListener('mousemove', onMouseMove)
            document.removeEventListener('mouseup', onMouseUp)
        }
        document.addEventListener('mousemove', onMouseMove)
        document.addEventListener('mouseup', onMouseUp)
    }, [])

    return (
        <>
            <EditorWrapper ref={wrapperRef} $rows={rows} $height={height}>
                <EditorContent as="div" $rows={rows}>
                    <RichTextPlugin
                        contentEditable={<ContentEditable style={{ outline: 'none', minHeight: 'inherit' }} />}
                        placeholder={<Placeholder text={placeholder || ''} />}
                        ErrorBoundary={LexicalErrorBoundary}
                    />
                    <OnChangePlugin onChange={onChange} />
                    <InitValuePlugin value={value} />
                </EditorContent>
            </EditorWrapper>
            {resizable && <ResizeHandle onMouseDown={handleMouseDown} />}
        </>
    )
}
